import moment from "moment";
import React, { useEffect, useState } from "react";
import { useMount, useTitle, useUpdateEffect } from "react-use";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useMutation, useQueryClient } from "react-query";
import Modal from "react-modal";
import { useNavigate } from "react-router-dom";
import { SITE_URL } from "../../api/common";
import Icon from "../../components/common/Icon";
import { Button, LinkButton } from "../../components/common/Forms";
import {
  lectureCurriculumState,
  lectureDateState,
  lectureHostState,
  lectureInfoState,
  lectureItemState,
  lectureListState,
  lecturePlaceState,
  lectureReportState,
  lectureTitleState,
} from "../../states/atoms/lectureState";
import { deleteLecture, getLecture } from "../../api";
import LectureView from "../../components/lecture/LectureView";
import { getStatusBadge } from "../../util/common";
import { signItem } from "../../states/atoms/signState";

const LectureListPage = () => {
  useTitle("THE MAKER 강의 목록");
  const [lectureList, setLectureList] = useRecoilState(lectureListState);
  const signInfo = useRecoilValue(signItem);
  const setItem = useSetRecoilState(lectureItemState);
  const setTitle = useSetRecoilState(lectureTitleState);
  const setDate = useSetRecoilState(lectureDateState);
  const setPlace = useSetRecoilState(lecturePlaceState);
  const setHost = useSetRecoilState(lectureHostState);
  const setInfo = useSetRecoilState(lectureInfoState);
  const setCurriculum = useSetRecoilState(lectureCurriculumState);
  const setReport = useSetRecoilState(lectureReportState);
  const [auth, setAuth] = useState(false);
  const [{ headers, sub_headers, contents }, setData] = useState({
    headers: [],
    sub_headers: [],
    contents: [],
    widths: [],
  });
  const [{ sort, asc }, setSort] = useState({ sort: null, asc: true });
  const [modal, setOpen] = useState({ open: false, data: null });
  const [data, setQueryResult] = useState(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const deleteMttn = useMutation({
    mutationFn: deleteLecture,
    onSettled: (data) => {
      const { result, error } = data;
      if (result) {
        getListQuery();
        handleClose();
      }
      if (error) {
        alert(error);
      }
    },
  });

  const getAttendanceButton = (id) => {
    return {
      value: 0,
      label: (
        <Button
          type="primary"
          size="xs"
          onClick={() => window.open(`/attendance/${id}`, "_blank")}
        >
          출석부
        </Button>
      ),
    };
  };

  const handleModalOpen = (data) => {
    const { start_date, end_date, ...rest } = data;
    setOpen({
      open: true,
      data: {
        ...rest,
        date: [new moment(start_date), new moment(end_date)],
      },
    });
  };

  const getListQuery = async () => {
    const result = await queryClient.fetchQuery(["lecture/list"], {
      queryFn: () => getLecture({ sort, asc }),
    });
    setQueryResult(result);
    setItem(null);
    queryClient.invalidateQueries({ queryKey: ["lecture/list"] });
  };

  useMount(() => {
    getListQuery();
  });

  useUpdateEffect(() => {
    if (data.result) {
      setLectureList(data.data);
    }
  }, [data]);

  useEffect(() => {
    if (signInfo && "info" in signInfo) {
      if (signInfo.info && "role" in signInfo.info) {
        const { role } = signInfo.info;
        return setAuth(role === "admin");
      }
    }
    setAuth(false);
  }, [signInfo]);

  useUpdateEffect(() => {
    const contents = lectureList.map((listitem) => {
      const { _id: id, title, start_date: sd, end_date: ed, host } = listitem;
      const start_date = new moment(sd).format("YYYY-MM-DD HH:mm");
      const end_date = new moment(ed).format("YYYY-MM-DD HH:mm");
      return {
        key: `row_${id}`,
        item: [
          { key: `r${id}_cell_id`, value: id, label: "", width: "" },
          {
            key: `r${id}_cell_title`,
            value: title,
            width: "min-w-[15rem]",
            label: (
              <LinkButton
                onClick={() =>
                  handleModalOpen({
                    ...listitem,
                    id,
                    title,
                    start_date,
                    end_date,
                    host,
                  })
                }
              >
                {title}
              </LinkButton>
            ),
          },
          {
            ...getAttendanceButton(id),
            key: `r${id}_cell_attendance`,
            width: "w-auto text-center",
          },
          {
            ...getStatusBadge(start_date, end_date),
            key: `r${id}_cell_status`,
            width: "min-w-[4rem] text-center",
          },
          {
            key: `r${id}_cell_start_date`,
            value: new moment(sd).valueOf(),
            label: start_date,
            width: "min-w-[6rem] w-32 text-center",
          },
          {
            key: `r${id}_cell_end_date`,
            value: new moment(ed).valueOf(),
            label: end_date,
            width: "min-w-[6rem] w-32 text-center",
          },
          {
            key: `r${id}_cell_quiz`,
            value: 0,
            label: (
              <Button
                type="primary"
                size="xs"
                onClick={() => navigate(`/quiz?lecture=${id}`)}
                outlined
              >
                관리
              </Button>
            ),
            width: "w-auto text-center",
          },
          {
            key: `r${id}_cell_survey`,
            value: 0,
            label: (
              <Button
                type="primary"
                size="xs"
                onClick={() => navigate(`/survey?lecture=${id}`)}
                outlined
              >
                관리
              </Button>
            ),
            width: "w-auto text-center",
          },
          {
            key: `r${id}_cell_certificate`,
            value: host,
            label: (
              <Button
                type="primary"
                size="xs"
                onClick={() => navigate(`/certificate/upsert?lecture=${id}`)}
                outlined
              >
                관리
              </Button>
            ),
            width: "w-auto text-center",
          },
        ],
      };
    });
    const headers = [
      {
        key: "title",
        label: "강의 이름",
        rowSpan: 2,
        width: "w-60",
        hasSub: false,
      },
      {
        key: "attendance",
        label: "출결 관리",
        rowSpan: 2,
        width: "w-auto min-w-fit",
        hasSub: false,
      },
      {
        key: "status",
        label: "상태",
        rowSpan: 2,
        width: "w-auto min-w-fit",
        hasSub: false,
      },
      { key: "date", label: "강의 일정", colSpan: 2, hasSub: true },
      {
        key: "quiz",
        label: "퀴즈",
        rowSpan: 2,
        width: "w-auto min-w-fit",
        hasSub: false,
      },
      { key: "survey", label: "설문", rowSpan: 2, hasSub: false },
      {
        key: "certificate",
        label: "수료증",
        rowSpan: 2,
        width: "w-auto min-w-fit",
        hasSub: false,
      },
    ];
    const sub_headers = [
      { key: "start_date", label: "시작일자", width: "w-32" },
      { key: "end_date", label: "종료일자", width: "w-32" },
    ];

    setData({
      headers,
      sub_headers,
      contents,
    });
    queryClient.invalidateQueries("lecture/list");
  }, [lectureList]);

  useUpdateEffect(() => {
    getListQuery();
  }, [sort, asc]);

  const handleSort =
    (key, hasSub = false) =>
    () => {
      if (hasSub) return;
      if (sort === key) {
        setSort({ sort, asc: !asc });
      } else {
        setSort({ sort: key, asc: true });
      }
    };

  const handleClose = () => {
    setOpen({ open: false, data: null });
  };

  const handleUpdate = () => {
    handleClose();
    navigate(`/lecture/update/${modal.data.id}`);
  };

  const handleDelete = () => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm("이 강의를 삭제하시겠습니까?")) {
      deleteMttn.mutate(modal.data.id);
    }
  };

  const handleLink = async () => {
    await navigator.clipboard.writeText(
      `${SITE_URL()}lecture/view/${modal.data.id}`
    );
    alert("클립보드에 복사되었습니다.");
  };

  const handleDuplicate = () => {
    const { id, title, date, place, host, info, curriculum, report } =
      modal.data;
    setItem(id);
    setTitle(title);
    const [sd, ed] = date;
    setDate([
      {
        date: sd.format("YYYY-MM-DD"),
        hour: sd.get("hour"),
        minute: sd.get("minute"),
      },
      {
        date: ed.format("YYYY-MM-DD"),
        hour: ed.get("hour"),
        minute: ed.get("minute"),
      },
    ]);
    setPlace(place);
    setHost(host);
    setInfo(info);
    setCurriculum(curriculum);
    setReport(report);
    handleClose();
    navigate(`/lecture/add`);
  };

  return (
    <div className="w-full mx-auto md:w-11/12 md:px-2">
      <h1 className="flex items-center font-sans font-bold break-normal text-cyan-500 px-2 py-4 text-xl md:text-2xl">
        강의 정보
      </h1>
      <div className="p-4 md:p-8 lg:mt-0 rounded shadow bg-white overflow-scroll scrollbar-hide relative">
        {auth && (
          <div className="w-full inline-flex justify-end items-center mb-2 sticky top-0 left-0 right-0">
            <Button
              type="primary"
              size="sm"
              onClick={() => navigate(`/lecture/add`)}
            >
              <Icon name="plus" size="sm" />
              강의 추가
            </Button>
          </div>
        )}
        <table className="stripe hover w-full py-4 text-sm table-auto border border-collapse border-zinc-400 cursor-default">
          <thead className="rounded-t">
            <tr className="cursor-default">
              {headers.length > 0 &&
                headers.map(({ key, label, width, hasSub, ...rest }, i) => (
                  <th
                    {...{
                      ...rest,
                      key: `${key}_${i}`,
                      className: `${width || "w-auto min-w-16"} ${
                        hasSub ? "py-1" : "py-2"
                      } border border-zinc-200 bg-zinc-100`,
                      onClick: handleSort(key, hasSub),
                    }}
                  >
                    <div className="inline-flex items-center">
                      {label}
                      {sort === key && (
                        <Icon
                          name={asc ? "bars-arrow-down" : "bars-arrow-up"}
                          size="sm"
                          className="ml-1"
                        />
                      )}
                    </div>
                  </th>
                ))}
            </tr>
            <tr>
              {sub_headers.length > 0 &&
                sub_headers.map(({ key, label, width }) => (
                  <th
                    {...{
                      key,
                      className: `${
                        width || "w-auto"
                      } border border-zinc-200 py-1 bg-zinc-100`,
                      onClick: handleSort(key),
                    }}
                  >
                    <div className="inline-flex items-center w-">
                      {label}
                      {sort === key && (
                        <Icon
                          name={asc ? "bars-arrow-down" : "bars-arrow-up"}
                          size="sm"
                          className="ml-1"
                        />
                      )}
                    </div>
                  </th>
                ))}
            </tr>
          </thead>
          <tbody className="rounded-b">
            {contents.length === 0 && (
              <tr>
                <td colSpan={11} className="border border-zinc-300">
                  <div className="inline-flex text-warning text-lg align-middle p-2 gap-2 mx-2">
                    <Icon name="exclamation-triangle" />
                    <h3>등록된 강의가 없습니다.</h3>
                  </div>
                </td>
              </tr>
            )}
            {contents.length > 0 &&
              contents.map(({ key, item }) => (
                <tr key={key}>
                  {item.map(
                    ({ key, width, label }) =>
                      label && (
                        <td
                          {...{
                            key,
                            className: `border border-zinc-300 p-1 ${width}`,
                          }}
                        >
                          {label}
                        </td>
                      )
                  )}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <Modal
        ariaHideApp={false}
        className="h-full text-center flex"
        isOpen={modal.open}
        onRequestClose={handleClose}
      >
        <div className="card w-9/12 bg-white shadow-xl m-auto min-h-[50vh] max-h-[83.4vh]">
          <div className="card-body overflow-hidden scale-90 m-[-2rem]">
            <div className="text-start h-full overflow-scroll scrollbar-hide border border-zinc-200 rounded-md ">
              {modal.data && <LectureView {...modal.data} />}
            </div>
            <div className="card-actions mt-4 w-full">
              <div className="inline-flex gap-2 w-full">
                <Button
                  type="primary"
                  className="flex-auto"
                  outlined
                  onClick={handleLink}
                >
                  <Icon
                    name="clipboard-document"
                    size="sm"
                    className="inline-block"
                  />
                  강의 정보 URL
                </Button>
                <Button
                  type="accent"
                  className="flex-auto"
                  onClick={handleUpdate}
                >
                  <Icon
                    name="pencil-square"
                    size="sm"
                    className="inline-block"
                  />
                  수정
                </Button>
                {auth && (
                  <Button
                    type="error"
                    className="flex-auto"
                    onClick={handleDelete}
                  >
                    <Icon
                      name="pencil-square"
                      size="sm"
                      className="inline-block"
                    />
                    삭제
                  </Button>
                )}
                <Button
                  type="primary"
                  className="flex-auto"
                  onClick={handleDuplicate}
                >
                  <Icon
                    name="document-duplicate"
                    size="sm"
                    className="inline-block"
                  />
                  복제
                </Button>
              </div>
              <Button
                className="w-full bg-zinc-300 border-0 text-black hover:bg-zinc-400"
                onClick={handleClose}
              >
                <Icon name="x" size="sm" className="inline-block" />
                닫기
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LectureListPage;
